/* import __COLOCATED_TEMPLATE__ from './question-generation-banner.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { ResponseError } from 'embercom/lib/inbox/requests';
import {
  type PlaygroundQuestionGenerationJobWireFormat,
  QuestionGenerationStatus,
} from 'embercom/lib/fin-playground/question-generation';
import type PlaygroundApiService from 'embercom/services/playground-api';

interface QuestionGenerationBannerArgs {}

type QuestionGenerationBannerSignature = {
  Args: QuestionGenerationBannerArgs;
};

export default class QuestionGenerationBanner extends Component<QuestionGenerationBannerSignature> {
  @service declare appService: $TSFixMe;
  @service declare intl: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare playgroundApi: PlaygroundApiService;

  get shouldShowBanner(): boolean {
    return (
      this.isRunning || this.needsFailureAcknowledgement || this.needsEmptyResultAcknowledgement
    );
  }

  get requiresUserAcknowledgement(): boolean {
    return (
      this.playgroundApi.playground?.questionGeneration.latestJobRequiresUserAcknowledgement ??
      false
    );
  }

  get status(): QuestionGenerationStatus | undefined {
    return this.playgroundApi.playground?.questionGeneration.latestJobStatus;
  }

  get isRunning(): boolean {
    return this.status === QuestionGenerationStatus.Running;
  }

  get needsEmptyResultAcknowledgement(): boolean {
    return (
      this.status === QuestionGenerationStatus.EmptyResult &&
      this.requiresUserAcknowledgement === true
    );
  }

  get needsFailureAcknowledgement(): boolean {
    return (
      this.status === QuestionGenerationStatus.Failed && this.requiresUserAcknowledgement === true
    );
  }

  @action
  updateQuestionGenerationJob(json: PlaygroundQuestionGenerationJobWireFormat) {
    this.playgroundApi.playground?.questionGeneration.updateLatestJobData(json);
  }

  @action
  resetQuestionGenerationJob() {
    this.playgroundApi.playground?.questionGeneration.resetLatestJobData();
  }

  @action
  cancelQuestionGeneration() {
    taskFor(this._cancelQuestionGenerationTask).perform(this.playgroundApi.playground?.id);
  }

  @action
  hideQuestionGenerationBanner() {
    taskFor(this._acknowledgeQuestionGenerationJob).perform(this.playgroundApi.playground?.id);
  }

  @dropTask
  *_cancelQuestionGenerationTask(playgroundId?: number) {
    try {
      yield this.playgroundApi.cancelQuestionGeneration(playgroundId);

      this.resetQuestionGenerationJob();
    } catch (error) {
      // If there is no job to cancel we will receive 409
      if (error instanceof ResponseError && error.response.status === 409) {
        this.resetQuestionGenerationJob();
      } else if (error instanceof ResponseError && error.response.status === 404) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.group-deleted-error'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.generating-questions.cancel-error'),
        );
      }
    }
  }

  @dropTask
  *_acknowledgeQuestionGenerationJob(groupId?: number) {
    try {
      let response = (yield this.playgroundApi.acknowledgeQuestionGeneration(groupId)) as Response;

      let json = (yield response.json()) as PlaygroundQuestionGenerationJobWireFormat;
      this.updateQuestionGenerationJob(json);
    } catch (error) {
      // If there is no job to acknowledge we will receive 409
      if (error instanceof ResponseError && error.response.status === 409) {
        this.resetQuestionGenerationJob();
      } else if (error instanceof ResponseError && error.response.status === 404) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.group-deleted-error'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.generating-questions.acknowledge-error'),
        );
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::QuestionGenerationBanner': typeof QuestionGenerationBanner;
  }
}
